import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  BarChart3,
  FileText,
  Users,
  Target,
  DollarSign,
  Search,
  RefreshCw,
  Activity,
  PieChart,
  Zap
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function AnalyticsDashboard() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("7d");
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [contentData, setContentData] = useState(null);
  const [seoData, setSeoData] = useState(null);
  const [roiData, setRoiData] = useState(null);
  const [teamData, setTeamData] = useState(null);

  useEffect(() => {
    loadAllData();
  }, [period]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [overviewRes, contentRes, seoRes, roiRes, teamRes] = await Promise.all([
        axios.get(`${API}/analytics-dashboard/overview?period=${period}`, { withCredentials: true }),
        axios.get(`${API}/analytics-dashboard/content-performance?period=${period}`, { withCredentials: true }),
        axios.get(`${API}/analytics-dashboard/seo-performance?period=${period}`, { withCredentials: true }),
        axios.get(`${API}/analytics-dashboard/marketing-roi?period=${period}`, { withCredentials: true }),
        axios.get(`${API}/analytics-dashboard/team-activity?period=${period}`, { withCredentials: true })
      ]);
      
      setOverview(overviewRes.data.overview);
      setContentData(contentRes.data);
      setSeoData(seoRes.data);
      setRoiData(roiRes.data);
      setTeamData(teamRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Please login to view analytics");
        navigate("/");
      } else {
        toast.error("Failed to load analytics data");
      }
    } finally {
      setLoading(false);
    }
  };

  const TrendIndicator = ({ value }) => {
    if (value === 0) return null;
    return value > 0 ? (
      <span className="flex items-center text-green-600 text-sm">
        <TrendingUp className="w-4 h-4 mr-1" />
        +{value}%
      </span>
    ) : (
      <span className="flex items-center text-red-600 text-sm">
        <TrendingDown className="w-4 h-4 mr-1" />
        {value}%
      </span>
    );
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "high": return "bg-red-100 text-red-700";
      case "medium": return "bg-yellow-100 text-yellow-700";
      case "low": return "bg-blue-100 text-blue-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate("/")}
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <span className="text-xl font-bold bg-gradient-to-r from-violet-700 to-purple-600 bg-clip-text text-transparent">
                MarkeeMix
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-36" data-testid="period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Last 7 Days</SelectItem>
                  <SelectItem value="30d">Last 30 Days</SelectItem>
                  <SelectItem value="90d">Last 90 Days</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={loadAllData} variant="outline" size="sm" data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-purple-600" />
            Advanced Analytics Dashboard
          </h1>
          <p className="text-gray-600 mt-1">
            Comprehensive insights across all your marketing activities
          </p>
        </div>

        {/* Overview Cards */}
        {overview && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="p-6 bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">{overview.content_generated.label}</p>
                  <p className="text-3xl font-bold text-purple-700">{overview.content_generated.value}</p>
                  <TrendIndicator value={overview.content_generated.trend} />
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">{overview.seo_audits.label}</p>
                  <p className="text-3xl font-bold text-blue-700">{overview.seo_audits.value}</p>
                  <TrendIndicator value={overview.seo_audits.trend} />
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Search className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">{overview.leads_captured.label}</p>
                  <p className="text-3xl font-bold text-green-700">{overview.leads_captured.value}</p>
                  <TrendIndicator value={overview.leads_captured.trend} />
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Target className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600 font-medium">{overview.active_teams.label}</p>
                  <p className="text-3xl font-bold text-orange-700">{overview.active_teams.value}</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* SEO Performance */}
          {seoData && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Search className="w-5 h-5 text-blue-600" />
                  SEO Performance
                </h2>
                <Badge variant="outline">{seoData.total_audits} Audits</Badge>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-600">SEO Score</p>
                  <p className={`text-3xl font-bold ${getScoreColor(seoData.average_scores.seo)}`}>
                    {seoData.average_scores.seo}
                  </p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600">Performance</p>
                  <p className={`text-3xl font-bold ${getScoreColor(seoData.average_scores.performance)}`}>
                    {seoData.average_scores.performance}
                  </p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-gray-600">Technical</p>
                  <p className={`text-3xl font-bold ${getScoreColor(seoData.average_scores.technical)}`}>
                    {seoData.average_scores.technical}
                  </p>
                </div>
              </div>

              <h3 className="font-medium mb-3">Common Issues</h3>
              <div className="space-y-2">
                {seoData.common_issues.slice(0, 4).map((issue, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm">{issue.issue}</span>
                    <div className="flex items-center gap-2">
                      <Badge className={getSeverityColor(issue.severity)}>{issue.severity}</Badge>
                      <span className="text-sm font-medium">{issue.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Marketing ROI */}
          {roiData && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  Marketing ROI
                </h2>
                <Badge variant="outline" className="bg-green-50">
                  {roiData.summary.overall_roi}% ROI
                </Badge>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-gray-600">Total Spend</p>
                  <p className="text-xl font-bold text-red-600">
                    ₹{roiData.summary.total_spend.toLocaleString()}
                  </p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600">Revenue</p>
                  <p className="text-xl font-bold text-green-600">
                    ₹{roiData.summary.total_revenue.toLocaleString()}
                  </p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-600">Profit</p>
                  <p className="text-xl font-bold text-blue-600">
                    ₹{(roiData.summary.total_revenue - roiData.summary.total_spend).toLocaleString()}
                  </p>
                </div>
              </div>

              <h3 className="font-medium mb-3">Channel Performance</h3>
              <div className="space-y-2">
                {roiData.channel_performance.map((channel, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium">{channel.channel}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-gray-500">
                        ₹{channel.spend.toLocaleString()} spent
                      </span>
                      <Badge className={channel.roi > 100 ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>
                        {channel.roi}% ROI
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Content Performance */}
          {contentData && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  Content Performance
                </h2>
              </div>

              <h3 className="font-medium mb-3">Top Topics</h3>
              <div className="space-y-3 mb-6">
                {contentData.top_topics.map((topic, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-violet-500"
                        style={{ width: `${(topic.count / contentData.top_topics[0].count) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm w-32 truncate">{topic.topic}</span>
                    <span className="text-sm font-medium w-8 text-right">{topic.count}</span>
                  </div>
                ))}
              </div>

              <h3 className="font-medium mb-3">Content by Type</h3>
              <div className="flex flex-wrap gap-2">
                {contentData.content_by_type.map((type, idx) => (
                  <Badge key={idx} variant="outline" className="py-2">
                    {type._id || "Other"}: {type.count}
                  </Badge>
                ))}
              </div>
            </Card>
          )}

          {/* Team Activity */}
          {teamData && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Activity className="w-5 h-5 text-orange-600" />
                  Team Activity
                </h2>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <p className="text-sm text-gray-600">Teams</p>
                  <p className="text-3xl font-bold text-orange-600">{teamData.summary.total_teams}</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-600">Members</p>
                  <p className="text-3xl font-bold text-blue-600">{teamData.summary.total_members}</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600">Activities</p>
                  <p className="text-3xl font-bold text-green-600">{teamData.summary.recent_activities}</p>
                </div>
              </div>

              {teamData.teams.length > 0 && (
                <>
                  <h3 className="font-medium mb-3">Your Teams</h3>
                  <div className="space-y-2">
                    {teamData.teams.map((team, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-red-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                            {team.name.charAt(0)}
                          </div>
                          <span className="font-medium">{team.name}</span>
                        </div>
                        <Badge variant="outline">{team.members} members</Badge>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {teamData.teams.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No teams yet. Create a team to collaborate!</p>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
